import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "biorlabs.activeProduct";
const VALID_PRODUCTS = ["biorvault", "biorswap"];

const ProductContext = createContext({
  product: "biorvault",
  isFlipping: false,
  setProduct: () => {},
  toggleProduct: () => {},
});

function readInitialProduct() {
  if (typeof window === "undefined") return "biorvault";
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("p");
    if (fromQuery && VALID_PRODUCTS.includes(fromQuery)) return fromQuery;
    const stored = window.sessionStorage?.getItem(STORAGE_KEY);
    if (stored && VALID_PRODUCTS.includes(stored)) return stored;
  } catch (_) {}
  return "biorvault";
}

export function ProductProvider({ children }) {
  const [product, setProductState] = useState(readInitialProduct);
  const [isFlipping, setIsFlipping] = useState(false);
  const productRef = useRef(product);
  const timersRef = useRef([]);

  useEffect(() => {
    productRef.current = product;
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, product);
    } catch (_) {}
  }, [product]);

  useEffect(() => () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
  }, []);

  const setProduct = useCallback((next) => {
    if (!VALID_PRODUCTS.includes(next)) return;
    if (productRef.current === next) return;
    // Clear any pending timers from a previous flip in progress
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    // Sequential fade: 500ms fade-out on the outgoing pane, then 500ms
    // fade-in on the incoming pane (CSS handles the staging via
    // transition-delay). isFlipping stays true for the full 1000ms so the
    // toggle buttons can't trigger a second flip mid-transition.
    setIsFlipping(true);
    setProductState(next);
    productRef.current = next;
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch (_) {
      window.scrollTo(0, 0);
    }
    const t = window.setTimeout(() => setIsFlipping(false), 1000);
    timersRef.current.push(t);
  }, []);

  const toggleProduct = useCallback(() => {
    setProduct(productRef.current === "biorvault" ? "biorswap" : "biorvault");
  }, [setProduct]);

  return (
    <ProductContext.Provider value={{ product, isFlipping, setProduct, toggleProduct }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProduct() {
  return useContext(ProductContext);
}
